import useMaterial from "@/hooks/useMaterial";
import Plank from "../Plank";
import { useLayoutEffect, useState, useRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import { Geometry, Base, Addition, Subtraction, ReverseSubtraction, Intersection, Difference } from "@react-three/csg";
import { RoundedBox } from "@react-three/drei";
import { useAppStore } from "@/stores/appStore";
import { materialOptions } from "@/theme/constants";
import { rotateUVs } from "@/services/helpers/rotateUV";
import { useContent } from "@/hooks/useContent";
import { config_base } from "@/data/config_base";
import Mailbox from "../Modifiers/Mailbox";
import useCopyTexture from "@/hooks/useCopyTexture";

const PillarTop = ({ doorWidth, pillarBase, material, extraWidth = 0.02 }) => {
	const { pillar } = useAppStore((state) => state.configurator);
	const width = pillarBase.width + extraWidth * 2;
	const length = pillarBase.length + extraWidth;
	const shape = new THREE.Shape();
	const bevel = 0.01;

	shape.moveTo(0, 0);
	shape.lineTo(0, length);
	shape.lineTo(width, length);
	shape.lineTo(width, 0);
	shape.lineTo(0, 0);

	const extrudeSettings = {
		steps: 2,
		depth: 0.001,
		bevelEnabled: true,
		bevelThickness: 0.005,
		bevelSize: 0.002,
		bevelOffset: 0,
		bevelSegments: 10,
	};

	const defaultMaterial = config_base.configurator.materials.find((material) => material.id === "concrete");

	const textures = useCopyTexture(defaultMaterial.textures, {
		repeat: defaultMaterial.settings.repeat,
	});

	const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

	rotateUVs(geometry, {
		front: 0,
		left: 0,
		right: 0,
		back: 0,
		top: 90,
		bottom: 0,
	});

	geometry.rotateX(Math.PI / 2);

	return (
		<mesh position={[0, pillarBase.height, -(length + bevel * 3) / 2]}>
			<Geometry>
				<Base geometry={geometry} />
			</Geometry>
			<meshStandardMaterial {...textures} />
		</mesh>
	);
};

const PillarBase = ({ pillarBase, position, minGap, extraWidth = 0.02 }) => {
	const meshRef = useRef();
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);

	const defaultMaterial = config_base.configurator.materials.find((material) => material.id === "brick_wall");

	const textures = useCopyTexture(defaultMaterial.textures, {
		repeat: {
			x: 2,
			y: 2,
		},
	});

	useEffect(() => {
		const mesh = meshRef.current;
		if (!mesh) return;

		const geometry = mesh.geometry;
		if (!geometry) return;

		// Check if the geometry is new
		if (lastGeometryRef.current !== geometry) {
			rotateUVs(geometry, {
				front: 180,
				left: 270,
				right: 270,
				back: 180,
				top: 0,
				bottom: 0,
			});

			// Store the new geometry reference
			lastGeometryRef.current = geometry;
		}
	}, [pillarBase.width, pillarBase.height, pillarBase.length]);

	return (
		<RoundedBox
			args={[pillarBase.width, pillarBase.height, pillarBase.length]}
			radius={0.0008}
			bevelSegments={2}
			smoothness={0.0008}
			position={position}
			ref={meshRef}
			castShadow
			receiveShadow>
			<meshStandardMaterial {...textures} />
		</RoundedBox>
	);
};

const PillarFoot = ({ pillarFoot, position, minGap, extraWidth = 0.02 }) => {
	const meshRef = useRef();
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);
	const { pillar } = useAppStore((state) => state.configurator);

	const defaultMaterial = config_base.configurator.materials.find((material) => material.id === "concrete");

	const textures = useCopyTexture(defaultMaterial.textures, {
		repeat: pillar.selectedMaterials["pillar_foot"]?.settings.repeat,
	});

	useEffect(() => {
		const mesh = meshRef.current;
		if (!mesh) return;

		const geometry = mesh.geometry;
		if (!geometry) return;

		// Check if the geometry is new
		if (lastGeometryRef.current !== geometry) {
			rotateUVs(geometry, {
				front: 180,
				left: 270,
				right: 270,
				back: 180,
				top: 180,
				bottom: 0,
			});

			// Store the new geometry reference
			lastGeometryRef.current = geometry;
		}
	}, [pillarFoot.width, pillarFoot.height, pillarFoot.length]);

	return (
		<>
			<RoundedBox
				args={[pillarFoot.width, pillarFoot.height, pillarFoot.length]}
				radius={0}
				position={position}
				ref={meshRef}>
				<meshStandardMaterial {...textures} />
			</RoundedBox>
		</>
	);
};

const getActiveModifiers = (activeModifiers, pillarBase, pillarFoot, minGap, extraWidth, pillarPosition) => {
	const modifiers = {
		pillar_foot: () => (
			<PillarFoot
				pillarFoot={pillarFoot}
				position={[pillarBase.width / 2 + 0.02, pillarFoot.height / 2, -0.015]}
				minGap={minGap}
				extraWidth={extraWidth}
			/>
		),
		mailbox: () =>
			pillarPosition === "right" && (
				<Mailbox
					position={[pillarBase.width / 2 + 0.02 - 0.05, pillarBase.height / 2, pillarBase.length / 2 - 0.012]}
				/>
			),
	};
	return modifiers[activeModifiers.id]();
};

export default function PillarSanFrancisco({ id, doorWidth, outerHeight, pillarDimensions, pillarPosition }) {
	const { pillar } = useAppStore((state) => state.configurator);
	const { getModifiers } = useContent();

	let selectedModifiers = pillar.selectedModifiers.filter((selected) => pillar.modifiers.includes(selected.id));

	selectedModifiers = getModifiers().filter((modifier) =>
		selectedModifiers.some((selected) => selected.id === modifier.id)
	);

	const pillarBase = {
		width: pillarDimensions.pillar_width / 500,
		height: outerHeight + 0.06,
		length: pillarDimensions.pillar_length / 500,
	};

	const pillarFoot = {
		width: pillarBase.width + pillarDimensions.pillar_foot_thickness / 500,
		height: pillarDimensions.pillar_foot_height / 500,
		length: pillarBase.length + pillarDimensions.pillar_foot_thickness / 500,
	};

	return (
		<group position={[doorWidth + 0.002, 0, 0]}>
			<PillarTop doorWidth={doorWidth} pillarBase={pillarBase} />
			<PillarBase position={[pillarBase.width / 2 + 0.02, pillarBase.height / 2, -0.015]} pillarBase={pillarBase} />
			{selectedModifiers &&
				selectedModifiers.map((mod, index) =>
					getActiveModifiers(mod, pillarBase, pillarFoot, 0.001, 0.02, pillarPosition)
				)}
		</group>
	);
}
